import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { FaDownload, FaTrash } from "react-icons/fa";
import api from "../../../api";
import { Pill } from "../performance/MyKraKpiPerformancePage";
import { formatMonthKey } from "../performance/performanceFormat";
import { CategorySubcategoryFields } from "../shared/CategoryMasterFields";
import "../shared/formModal.css";
import { applyFieldRules, formatFieldValue, isFieldLocked, recordEvidenceMeta, recordValue, statusLabel } from "./kraWorkFormat";

// Add / edit / view one "My KRA Work" record. The form is rendered from the
// record type's server-side field schema (GET /analyst-work/meta), so the
// fields, required flags and options always match what the API validates.
// Schools come from the existing schools master (GET /schools); Category /
// Sub-category from the categories master (shared picker, with "+ Add New").
// Saving a record never submits anything for scoring -- that is "Prepare
// evidence" on the KRA work page, which goes to the reviewer.

async function downloadFile(url, name) {
  const res = await api.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name || "attachment";
  a.click();
  window.URL.revokeObjectURL(href);
}

function initialValues(typeMeta, record, month) {
  const values = {};
  for (const field of typeMeta.fields) {
    values[field.key] = recordValue(record, field);
    if (!record && field.type === "MONTH" && month) values[field.key] = month;
  }
  values.status = record?.status || typeMeta.statuses[0]?.value || "";
  return applyFieldRules(typeMeta.fields, values);
}

// Form sections: Classification (category master) and Evidence & remarks
// (links, Remarks); every other field is a record detail.
const isClassification = (f) => f.type === "CATEGORY" || f.type === "SUB_CATEGORY";
const isEvidence = (f) => f.type === "URL" || f.key === "remarks";

const ADD_NEW = "__add_new__";

// Pick-list with "+ Add new…" (`add_new` text fields): choose a listed value,
// or switch to a text box and type a new one. The choices are values already
// saved on records (server-side). A saved value missing from the list is
// still shown as selected.
function PickOrAdd({ id, value, options, disabled, maxLength, onChange, newLabel }) {
  const current = value === null || value === undefined ? "" : String(value);
  const [adding, setAdding] = useState(false);
  const choices = current && !options.some((o) => o.toLowerCase() === current.toLowerCase()) ? [...options, current] : options;
  if (adding || !choices.length) {
    return (
      <div className="kw-addnew">
        <Form.Control id={id} type="text" value={current} disabled={disabled} maxLength={maxLength || undefined}
          placeholder={`Type new ${newLabel.toLowerCase()}…`} autoFocus={adding} onChange={(e) => onChange(e.target.value)} />
        {choices.length > 0 && !disabled && (
          <button type="button" className="kp-link kw-addnew-back" onClick={() => { setAdding(false); onChange(""); }}>Choose from list</button>
        )}
      </div>
    );
  }
  return (
    <Form.Select id={id} value={current} disabled={disabled}
      onChange={(e) => { if (e.target.value === ADD_NEW) { setAdding(true); onChange(""); } else onChange(e.target.value); }}>
      <option value="">Select…</option>
      {choices.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value={ADD_NEW}>+ Add new…</option>
    </Form.Select>
  );
}

function FieldInput({ field, value, onChange, schools, disabled, max, hint }) {
  const id = `wr-${field.key}`;
  const label = <Form.Label>{field.label}{field.required && <span className="text-danger"> *</span>}</Form.Label>;
  if (field.type === "BOOLEAN") {
    return <Form.Check className="kw-check" id={id} type="checkbox" label={field.label} checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
  }
  let control;
  if (field.add_new) {
    control = (
      <PickOrAdd id={id} value={value} options={field.suggestions || []}
        disabled={disabled} maxLength={field.max_length} newLabel={field.label} onChange={onChange} />
    );
  } else if (field.type === "SELECT" || field.type === "SCHOOL") {
    const options = field.type === "SCHOOL" ? schools.map((s) => ({ value: String(s.id), label: s.name })) : field.options;
    control = (
      <Form.Select value={value === null || value === undefined ? "" : String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{field.type === "SCHOOL" ? "Select school…" : "Select…"}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Form.Select>
    );
  } else if (field.type === "TEXTAREA") {
    control = <Form.Control as="textarea" rows={3} value={value} maxLength={field.max_length || undefined} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    if (field.max_length && !hint) hint = `${String(value || "").length}/${field.max_length}`;
  } else {
    const inputType = { DATE: "date", DATETIME: "datetime-local", MONTH: "month", INTEGER: "number", NUMBER: "number", URL: "url" }[field.type] || "text";
    control = (
      <Form.Control
        type={inputType} value={value} disabled={disabled}
        min={field.type === "INTEGER" || field.type === "NUMBER" ? 0 : undefined}
        max={max}
        step={field.type === "INTEGER" ? 1 : field.type === "NUMBER" ? "any" : undefined}
        maxLength={field.max_length || undefined}
        placeholder={field.type === "URL" ? "https://…" : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <Form.Group controlId={id}>
      {label}{control}
      {hint && <Form.Text muted>{hint}</Form.Text>}
    </Form.Group>
  );
}

const WorkRecordModal = ({ typeMeta, record, month, schools = [], readOnly = false, onClose, onSaved }) => {
  const [values, setValues] = useState(() => initialValues(typeMeta, record, month));
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(!readOnly);
  const isNew = !record;
  const locked = !!record?.is_locked;
  const disabled = !editing || locked || saving;

  const categoryField = typeMeta.fields.find((f) => f.type === "CATEGORY");
  const subCategoryField = typeMeta.fields.find((f) => f.type === "SUB_CATEGORY");
  const detailFields = typeMeta.fields.filter((f) => !isClassification(f) && !isEvidence(f));
  const evidenceFields = typeMeta.fields.filter(isEvidence);

  useEffect(() => {
    if (!record?.id) return undefined;
    let cancelled = false;
    api.get(`/analyst-work/records/${record.id}`)
      .then((res) => { if (!cancelled) setAttachments(res.data?.data?.attachments || []); })
      .catch(() => { if (!cancelled) setAttachments([]); });
    return () => { cancelled = true; };
  }, [record?.id]);

  const missing = useMemo(
    () => typeMeta.fields.filter((f) => f.required && (values[f.key] === "" || values[f.key] === null || values[f.key] === undefined)),
    [typeMeta, values]
  );

  const setField = (key, v) => setValues((prev) => applyFieldRules(typeMeta.fields, { ...prev, [key]: v }, key));

  const save = async () => {
    if (missing.length) { setError(`Enter "${missing[0].label}".`); return; }
    setSaving(true);
    setError("");
    try {
      const body = { record_type: typeMeta.record_type, status: values.status };
      for (const field of typeMeta.fields) body[field.key] = values[field.key] === "" ? null : values[field.key];
      const res = isNew
        ? await api.post("/analyst-work/records", body)
        : await api.put(`/analyst-work/records/${record.id}`, body);
      const saved = res.data?.data;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/analyst-work/records/${saved.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved?.(saved);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save this record.");
    } finally {
      setSaving(false);
    }
  };

  const removeAttachment = async (id) => {
    try {
      await api.delete(`/analyst-work/attachments/${id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the attachment.");
    }
  };

  const evidence = record ? recordEvidenceMeta(record) : null;

  const renderField = (field) => {
    const fieldLocked = isFieldLocked(field, values);
    const capLabel = field.max_of && typeMeta.fields.find((f) => f.key === field.max_of)?.label;
    return (
      <div key={field.key} className={field.type === "TEXTAREA" ? "kw-span-2" : ""}>
        <FieldInput field={field} value={values[field.key]} schools={schools} disabled={disabled || fieldLocked}
          max={field.max_of && values[field.max_of] !== "" ? values[field.max_of] : undefined}
          hint={fieldLocked ? `0 — no ${String(capLabel || "incorrect records").toLowerCase()}.`
            : field.computed_from ? "Filled automatically (total − accurate)." : undefined}
          onChange={(v) => setField(field.key, v)} />
      </div>
    );
  };

  const readOnlyRows = (fields) => fields.map((field) => (
    <React.Fragment key={field.key}>
      <dt>{field.label}</dt>
      <dd>{field.type === "URL" && recordValue(record, field)
        ? <a href={recordValue(record, field)} target="_blank" rel="noopener noreferrer">{recordValue(record, field)}</a>
        : formatFieldValue(field, recordValue(record, field), { schools, record })}</dd>
    </React.Fragment>
  ));

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable size="lg" className="kp-modal" dialogClassName="kp-form-dialog">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="div">
          <div className="tlp-eyebrow">{typeMeta.label}{record ? ` · ${formatMonthKey(record.period_month)}` : ""}</div>
          <div className="kp-modal-title">{isNew ? "Add record" : record.title}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="kp-form-body">
        {record && (
          <div className="kw-modal-badges">
            <Pill meta={evidence} />
            {locked && <span className="kp-sub">Locked: this record is part of KPI evidence that is awaiting review or approved.</span>}
          </div>
        )}
        <p className="kp-sub mb-3">Counts toward: <strong>{typeMeta.kpi_hint}</strong>. {typeMeta.qualifying_note}</p>

        {!editing ? (
          <div className="kp-form">
            <section className="kp-form-section">
              <h3 className="kp-form-section-title">Record details</h3>
              <dl className="kw-details">
                {readOnlyRows(detailFields)}
                <dt>Status</dt>
                <dd>{statusLabel(typeMeta, record?.status)}</dd>
              </dl>
            </section>
            {categoryField && (
              <section className="kp-form-section">
                <h3 className="kp-form-section-title">Classification</h3>
                <dl className="kw-details">{readOnlyRows([categoryField, subCategoryField].filter(Boolean))}</dl>
              </section>
            )}
            {evidenceFields.length > 0 && (
              <section className="kp-form-section">
                <h3 className="kp-form-section-title">Evidence &amp; remarks</h3>
                <dl className="kw-details kw-details-pre">{readOnlyRows(evidenceFields)}</dl>
              </section>
            )}
          </div>
        ) : (
          <Form id="kw-record-form" className="kp-form" noValidate onSubmit={(e) => { e.preventDefault(); save(); }}>
            <section className="kp-form-section" aria-labelledby="kw-sec-details">
              <h3 id="kw-sec-details" className="kp-form-section-title">Record details</h3>
              <div className="kw-form-grid">
                {detailFields.map(renderField)}
                {typeMeta.statuses.length > 1 && (
                  <Form.Group controlId="wr-status">
                    <Form.Label>Status</Form.Label>
                    <Form.Select value={values.status} disabled={disabled} onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.value }))}>
                      {typeMeta.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                )}
              </div>
            </section>

            {categoryField && (
              <section className="kp-form-section" aria-labelledby="kw-sec-class">
                <h3 id="kw-sec-class" className="kp-form-section-title">Classification</h3>
                <div className="kw-form-grid">
                  <CategorySubcategoryFields
                    idPrefix="wr"
                    categoryLabel={categoryField.label} categoryRequired={!!categoryField.required}
                    categoryId={values[categoryField.key] || ""} savedCategoryName={record?.details?.category_name}
                    onCategoryChange={(id) => setField(categoryField.key, id || "")}
                    showSub={!!subCategoryField} subLabel={subCategoryField?.label} subRequired={!!subCategoryField?.required}
                    subValue={subCategoryField ? values[subCategoryField.key] || "" : ""}
                    savedSubNames={record?.details?.sub_category_id ? { [String(record.details.sub_category_id)]: record.details.sub_category_name } : {}}
                    onSubChange={(id) => setField(subCategoryField.key, id || "")}
                    disabled={disabled}
                  />
                </div>
              </section>
            )}

            <section className="kp-form-section" aria-labelledby="kw-sec-evidence">
              <h3 id="kw-sec-evidence" className="kp-form-section-title">Evidence &amp; remarks</h3>
              <div className="kw-form-grid">
                {evidenceFields.filter((f) => f.type === "URL").map(renderField)}
                {!locked && (
                  <Form.Group controlId="wr-files">
                    <Form.Label>Attach evidence</Form.Label>
                    <Form.Control type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={(e) => setFiles(Array.from(e.target.files || []))} disabled={saving} />
                    <Form.Text muted>PDF, image, Word, Excel or text — up to 10 MB each.</Form.Text>
                  </Form.Group>
                )}
                {evidenceFields.filter((f) => f.type !== "URL").map(renderField)}
              </div>
            </section>
          </Form>
        )}

        {attachments.length > 0 && (
          <div className="kw-attachments">
            <strong className="kp-sub">Attachments</strong>
            <ul className="mb-0">
              {attachments.map((a) => (
                <li key={a.id}>
                  <button type="button" className="kp-link" onClick={() => downloadFile(`/analyst-work/attachments/${a.id}/download`, a.original_name)}>
                    <FaDownload /> {a.original_name}
                  </button>
                  {editing && !locked && (
                    <button type="button" className="kp-link kw-remove" onClick={() => removeAttachment(a.id)} aria-label={`Remove ${a.original_name}`}>
                      <FaTrash />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0" role="alert">{error}</div>}
      </Modal.Body>
      <Modal.Footer className="kp-form-footer">
        {editing && !locked && missing.length > 0 && !saving && <span className="kp-footer-hint">Required: {missing.map((f) => f.label).join(", ")}</span>}
        <button type="button" className="tlp-btn tlp-btn-ghost" onClick={onClose} disabled={saving}>{editing ? "Cancel" : "Close"}</button>
        {!editing && record?.can_edit && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={() => setEditing(true)}>Edit</button>
        )}
        {editing && !locked && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={save} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : isNew ? "Save record" : "Save changes"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WorkRecordModal;
